'use client'

import { ArrowUpRightIcon } from 'lucide-react'
import Image from 'next/image'
import { useEffect, useRef } from 'react'
import { cn } from '~/lib/utils'

interface OfferBannerProps {
  className?: string
}

export function OfferBanner(props: OfferBannerProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const bannerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const banner = bannerRef.current
    const video = videoRef.current

    if (!banner || !video) {
      return
    }

    const handleMouseEnter = () => {
      video.play().catch(() => {
        // Ignore autoplay errors
      })
    }

    const handleMouseLeave = () => {
      video.pause()
      video.currentTime = 0
    }

    banner.addEventListener('mouseenter', handleMouseEnter)
    banner.addEventListener('mouseleave', handleMouseLeave)

    return () => {
      banner.removeEventListener('mouseenter', handleMouseEnter)
      banner.removeEventListener('mouseleave', handleMouseLeave)
    }
  }, [])

  return (
    <section ref={bannerRef} className={cn('group/banner', props.className)}>
      <a
        href="https://offer.gernar.ru?utm_source=gernar.ru&utm_medium=banner&utm_campaign=homepage_banner"
        target="_blank"
        rel="noopener noreferrer"
        className={`
          relative flex flex-col overflow-hidden rounded-xl
          focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 focus:outline-none
          md:flex-row md:items-center
        `}
      >
        {/* Background layers */}
        <div
          className="absolute inset-0 bg-gradient-to-br from-violet-600 via-purple-600 to-indigo-700"
          aria-hidden="true"
        />
        <Image
          src="/assets/offer-marketing/money-keyboard.jpg"
          alt=""
          fill
          sizes="100vw"
          className="object-cover object-center opacity-35"
          priority
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" aria-hidden="true" />
        <div
          className={`
            absolute inset-0 bg-[url('/assets/offer-marketing/gaussian-noise.png')] opacity-10 mix-blend-overlay
          `}
          aria-hidden="true"
        />

        {/* Decorative blurs */}
        <div
          className={`
            absolute -top-4 -right-4 h-24 w-24 rounded-full bg-white/25 blur-3xl
            sm:-top-6 sm:-right-6 sm:h-32 sm:w-32
            md:-top-8 md:-right-8 md:h-40 md:w-40
          `}
          aria-hidden="true"
        />
        <div
          className={`
            absolute -bottom-4 -left-4 h-24 w-24 rounded-full bg-purple-300/20 blur-3xl
            sm:-bottom-6 sm:-left-6 sm:h-32 sm:w-32
            md:-bottom-8 md:-left-8 md:h-40 md:w-40
          `}
          aria-hidden="true"
        />

        {/* Text content */}
        <div className="relative flex flex-1 flex-col gap-1 px-5 py-5 sm:px-6 sm:py-6 md:px-8 md:py-7">
          <h3 className="text-xl leading-tight font-bold text-white sm:text-2xl md:text-3xl">
            <span className="bg-gradient-to-r from-yellow-200 to-yellow-400 bg-clip-text text-transparent">
              Получи оффер под ключ
            </span>
          </h3>

          <p className="text-base text-pretty text-white/90 sm:text-lg md:text-xl">
            Персональный ментор помогает пройти весь путь. Работаем на результат, а не на обещания!
          </p>

          <div className="mt-3 md:hidden">
            <span className={`
              inline-flex items-center gap-1.5 rounded-lg border border-white/20 bg-white/10 px-4 py-2 text-sm
              font-semibold text-white backdrop-blur-sm
            `}
            >
              <span>Узнать подробнее</span>
              <ArrowUpRightIcon className="size-4" />
            </span>
          </div>

          <span className="mt-1 hidden items-center gap-1 text-base text-white/70 md:inline-flex">
            offer.gernar.ru
            <ArrowUpRightIcon className="size-4" />
          </span>
        </div>

        {/* Video preview — desktop only */}
        <div className="relative hidden shrink-0 p-4 md:block" aria-hidden="true">
          <div
            className={`
              relative aspect-video w-[240px] overflow-hidden rounded-xl bg-zinc-900 shadow-xl ring-2 ring-white
              transition-transform duration-300 ease-out
              group-hover/banner:scale-105
            `}
          >
            <p
              className={`
                absolute inset-0 z-3 flex flex-col items-center justify-end p-3 text-center transition-opacity
                duration-500
                group-hover/banner:opacity-0
              `}
            >
              <span className="text-sm font-medium text-white drop-shadow-lg">Трудоустройство под ключ</span>
            </p>

            <div className="absolute inset-0 z-3 flex items-center justify-center">
              <div className={`
                flex size-12 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm transition-transform
                ease-in-out
                group-hover/banner:scale-115 group-hover/banner:bg-black/20
              `}
              >
                <img
                  src="/assets/offer-marketing/play-button.svg"
                  alt=""
                  className="size-8"
                  width={32}
                  height={32}
                  loading="eager"
                />
              </div>
            </div>

            <div
              className="absolute inset-x-0 bottom-0 z-2 h-1/2 bg-gradient-to-t from-zinc-900/75 to-transparent"
            />

            <video
              ref={videoRef}
              src="/assets/offer-marketing/video-preview.mp4"
              muted
              playsInline
              loop
              className={`
                absolute inset-0 z-2 aspect-video size-full scale-[1.01] object-cover opacity-0 transition-opacity
                duration-500
                group-hover/banner:opacity-100
              `}
            />

            <Image
              src="/assets/offer-marketing/video-preview.jpg"
              alt=""
              fill
              sizes="240px"
              className={`
                z-1 object-cover transition-transform duration-500 ease-in-out
                group-hover/banner:scale-[1.02]
              `}
            />
          </div>
        </div>
      </a>
    </section>
  )
}
